/**
 * Переименование столбца из его меню на канбане. Макет data/Задачник.dc.html:
 * «⋯» в заголовке столбца → «Переименовать столбец».
 *
 * Прежде столбец переименовывался только в настройках доски, и на самой
 * доске действия со столбцом не было вовсе.
 */

import { useState } from "react";

import { errorMessage } from "@taskflow/client-core";
import { Button, FormField, Input, Modal, toast } from "@/shared/ui";

import {
  type BoardColumn,
  type BoardDetail,
  useUpdateColumnMutation,
} from "@taskflow/client-core/boards";

export function RenameColumnDialog({
  board,
  column,
  onClose,
}: {
  board: BoardDetail;
  column: BoardColumn;
  onClose: () => void;
}) {
  const [updateColumn, { isLoading }] = useUpdateColumnMutation();
  const [name, setName] = useState(column.name);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    const next = name.trim();
    if (next === "") {
      setError("Название не может быть пустым");
      return;
    }
    // Как в макете: два столбца с одним именем на доске неразличимы.
    const taken = board.columns.some(
      (item) =>
        item.isActive && item.id !== column.id && item.name.trim().toLowerCase() === next.toLowerCase(),
    );
    if (taken) {
      setError("Столбец с таким названием уже существует");
      return;
    }
    if (next === column.name) {
      onClose();
      return;
    }
    setError(null);
    try {
      await updateColumn({
        boardId: board.id,
        columnId: column.id,
        version: column.version,
        name: next,
      }).unwrap();
      toast.success("Столбец переименован", next);
      onClose();
    } catch (raw) {
      setError(errorMessage(raw));
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Переименовать столбец"
      width="sm"
      dismissable={!isLoading}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={isLoading}>
            Отмена
          </Button>
          <Button loading={isLoading} onClick={() => void submit()}>
            Сохранить
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <FormField label="Название" required>
          {(props) => (
            <Input
              {...props}
              value={name}
              autoFocus
              onChange={(event) => setName(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") void submit();
              }}
            />
          )}
        </FormField>
        {error !== null && (
          <p role="alert" className="text-[13px] font-semibold text-danger">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}
